<script setup>
import { ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const { accountId } = useAccount();

const isConnecting = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

const connectThreads = async () => {
  try {
    isConnecting.value = true;

    // Call Chatwoot backend to get Threads OAuth URL (Direct OAuth)
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/threads/authorizations`
    );

    const { url } = response.data;

    if (!url) {
      hasError.value = true;
      errorMessage.value = 'Could not generate Threads authorization URL';
      isConnecting.value = false;
      return;
    }

    // Redirect to Threads OAuth (Direct)
    window.location.href = url;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = error.response?.data?.error || error.message || 'Failed to connect to Threads';
    isConnecting.value = false;
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full">
      <!-- Error State -->
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5 class="text-xl font-semibold text-red-600 mb-2">Connection Error</h5>
        <p class="text-sm text-n-slate-11">{{ errorMessage }}</p>
      </div>

      <!-- Threads Connection -->
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <div
          class="w-20 h-20 mb-6 mx-auto rounded-full flex items-center justify-center text-white"
          style="background-color: #000000;"
        >
          <span class="i-ri-at-line text-5xl" />
        </div>
        <h6 class="text-2xl font-medium">
          Connect your Threads Account
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          Connect your Threads account to manage your posts and engage with replies
        </p>
        <button
          class="flex items-center justify-center px-6 py-3 text-white rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style="background-color: #000000;"
          :disabled="isConnecting"
          @click="connectThreads"
        >
          <span v-if="isConnecting" class="mr-2">
            <svg
              class="w-5 h-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          <span class="i-ri-at-line mr-2 text-xl" />
          <span class="font-medium">
            {{ isConnecting ? 'Connecting...' : 'Continue with Threads' }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
